import React, { createContext, useContext, useState, useEffect } from 'react';
import { getDatabase, ref, onValue, push, update, remove } from 'firebase/database';

const AssetsContext = createContext();

export const AssetsProvider = ({ children }) => {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assetsThisWeek, setAssetsThisWeek] = useState(0); // To store count of assets added this week

  const database = getDatabase();

  // Calculate start of the current week (Sunday 12:00 AM)
  const getStartOfWeek = () => {
    const now = new Date();
    const dayOfWeek = now.getDay();
    now.setHours(0, 0, 0, 0); // Set time to 12:00 AM
    now.setDate(now.getDate() - dayOfWeek); // Move to the most recent Sunday
    return now.getTime(); // Return timestamp of the start of the week
  };

  // Fetch assets from Firebase
  const fetchAssets = () => {
    const assetsRef = ref(database, 'Assets');
    onValue(assetsRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const assetsArray = Object.keys(data).map((key) => ({
          id: key,
          ...data[key],
        }));
        setAssets(assetsArray);

        // Calculate the assets added this week
        const startOfWeek = getStartOfWeek();
        const thisWeekCount = assetsArray.filter((asset) => asset.timestamp >= startOfWeek).length;
        setAssetsThisWeek(thisWeekCount);
      } else {
        setAssets([]);
      }
      setLoading(false);
    });
  };

  // Add a new asset
  const addAsset = async (newAsset) => {
    const assetsRef = ref(database, 'Assets');
    const newAssetRef = push(assetsRef);
    const timestamp = new Date().getTime(); // Get current timestamp for the new asset
    await newAssetRef.set({ ...newAsset, timestamp });
  };

  // Update an existing asset
  const updateAsset = async (id, updatedAsset) => {
    const assetRef = ref(database, `Assets/${id}`);
    await update(assetRef, updatedAsset);
  };

  // Delete an asset
  const deleteAsset = async (id) => {
    const assetRef = ref(database, `Assets/${id}`);
    await remove(assetRef);
    fetchAssets(); // Re-fetch assets to ensure the state is updated
  };

  useEffect(() => {
    fetchAssets();
  }, []);

  return (
    <AssetsContext.Provider value={{ assets, loading, assetsThisWeek, addAsset, updateAsset, deleteAsset }}>
      {children}
    </AssetsContext.Provider>
  );
};

// Custom hook to use the AssetsContext
export const useAssets = () => {
  return useContext(AssetsContext);
};
